// Reglas de pago del cobro en caja, del lado del cliente.
//
// Espejo declarado de `cashboxService.js` en `pos-graciela-api`
// (`PAYMENT_PRIORITY`, `CHANGE_ENABLED_METHODS`, `distributePayments`). El
// backend sigue siendo la autoridad: esto existe para que el cajero vea el
// vuelto mientras tipea y para no gastar un round trip hasta el servidor en un
// error previsible — SPEC_COBRO_PERFORMANCE_v0.1.md, RF-6/RF-7/RF-8.
//
// Extensión `.mjs` a propósito (PLAN, D8): permite testearlo con `node --test`
// sin agregar un runner ni dependencias al repo. Importar siempre con la
// extensión explícita: `@/utils/cashPayments.mjs`.

export const PAYMENT_METHODS = ['EFECTIVO', 'CHEQUE', 'TRANSFERENCIA', 'TARJETA'];
export const PAYMENT_METHOD_ALIASES = { POS: 'TARJETA' };

// El orden importa: el efectivo se imputa ANTES que la tarjeta, así que en un
// pago mixto con excedente el sobrante queda sobre la tarjeta —que no admite
// vuelto— y el backend rechaza la operación.
export const PAYMENT_PRIORITY = ['CHEQUE', 'EFECTIVO', 'TRANSFERENCIA', 'TARJETA'];
export const CHANGE_ENABLED_METHODS = ['EFECTIVO', 'CHEQUE'];

// Mismo umbral que `distributePayments`: un faltante menor a esto es redondeo,
// no un cobro insuficiente.
export const TOLERANCE = 0.009;

// Billetes de curso habitual en Paraguay.
export const QUICK_BILLS = [2000, 5000, 10000, 20000, 50000, 100000];

export function roundTwo(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export function normalizeMethod(value) {
  const method = String(value || '').trim().toUpperCase();
  return PAYMENT_METHOD_ALIASES[method] || method;
}

/** Acepta la forma del componente (`type`/`amount`) y la del API (`metodo_pago`/`monto`). */
function normalizeEntry(payment = {}) {
  return {
    method: normalizeMethod(payment.metodo_pago ?? payment.metodo ?? payment.type ?? payment.name),
    amount: roundTwo(payment.monto ?? payment.amount ?? payment.value ?? 0)
  };
}

/**
 * Resume un conjunto de pagos contra el total a cobrar.
 *
 * @returns {{
 *   assigned: number, remaining: number, change: number,
 *   sufficient: boolean, valid: boolean,
 *   invalidChangeMethods: string[], byMethod: Record<string, number>
 * }}
 */
export function summarizePayments({ payments = [], total = 0 } = {}) {
  const totalDue = roundTwo(total);
  const byMethod = {};

  for (const payment of payments) {
    const { method, amount } = normalizeEntry(payment);
    if (!method || !(amount > 0)) continue;
    byMethod[method] = roundTwo((byMethod[method] || 0) + amount);
  }

  const assigned = Object.values(byMethod).reduce((acc, value) => roundTwo(acc + value), 0);
  const remaining = roundTwo(totalDue - assigned);
  const sufficient = remaining <= TOLERANCE;

  // Imputación en el mismo orden que el backend, para calcular el excedente
  // exactamente sobre los mismos métodos.
  const orderedMethods = [...new Set([...PAYMENT_PRIORITY, ...Object.keys(byMethod)])];
  let pending = totalDue;
  let change = 0;
  const invalidChangeMethods = [];

  for (const method of orderedMethods) {
    const amount = byMethod[method] || 0;
    if (amount <= 0) continue;
    const applied = Math.min(amount, Math.max(pending, 0));
    pending = roundTwo(pending - applied);
    const excess = roundTwo(amount - applied);
    if (excess <= TOLERANCE) continue;
    if (CHANGE_ENABLED_METHODS.includes(method)) {
      change = roundTwo(change + excess);
    } else {
      invalidChangeMethods.push(method);
    }
  }

  return {
    assigned,
    remaining: Math.max(remaining, 0),
    change,
    sufficient,
    valid: sufficient && invalidChangeMethods.length === 0 && assigned > 0,
    invalidChangeMethods,
    byMethod
  };
}

/**
 * Billetes con los que tiene sentido pagar ese total: solo los mayores o
 * iguales. Ofrecer un billete de 2.000 para una compra de 45.000 no ayuda.
 */
export function suggestBills({ total = 0, bills = QUICK_BILLS } = {}) {
  const totalDue = roundTwo(total);
  return bills.filter((bill) => bill >= totalDue);
}

export default {
  PAYMENT_METHODS,
  PAYMENT_PRIORITY,
  CHANGE_ENABLED_METHODS,
  QUICK_BILLS,
  TOLERANCE,
  roundTwo,
  normalizeMethod,
  summarizePayments,
  suggestBills
};
